'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Loader2, Save, SquareDashedMousePointer } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ColorPickerPopover } from '@/components/color-picker-popover';
import {
  validateManualFieldDraft,
  type ManualFieldDraft,
  type ManualFieldDraftErrors
} from '../utils/manual-field-draft';

/**
 * What pressing the primary button does.
 *
 * `draw` is the toolbar flow: the operator picks the region on the document
 * afterwards, so this dialog closes and hands the draft over half-finished.
 * `save` is the per-page-section flow: there is no region and there will not
 * be one, so the field is written on the spot and the dialog is done.
 */
export type AddFieldMode = 'draw' | 'save';

interface AddFieldDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: AddFieldMode;
  /** `save` mode only — the create request is in flight. */
  submitting?: boolean;
  /**
   * The `ocr_default_highlight_color` app setting — the same default a field
   * with no template colour already falls back to, so a new field is tinted
   * like its neighbours unless the operator says otherwise.
   */
  defaultColor: string;
  /**
   * Called once the three typed inputs validate — the caller decides what that
   * means, per `mode`. The box is never collected here: the operator has to
   * draw it on the document, which is behind this dialog.
   */
  onSubmit: (draft: ManualFieldDraft) => void;
}

/**
 * Name, value and colour for a field an operator adds by hand (OMA-630).
 *
 * These three inputs are the whole field when it is added from a page section;
 * the toolbar flow adds a fourth, the region, drawn on the page this dialog
 * covers. Splitting that one out is deliberate — a modal cannot host a drag on
 * the document underneath it, and asking for the box first would mean holding
 * a geometry with nothing to attach it to.
 */
export function AddFieldDialog({
  open,
  onOpenChange,
  mode,
  submitting = false,
  defaultColor,
  onSubmit
}: AddFieldDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* The form lives one level down because Radix unmounts `DialogContent`
          on close: its state — a half-typed name, a stale error — is discarded
          by construction, instead of by an effect watching `open` to clear it. */}
      <DialogContent className='sm:max-w-md'>
        <AddFieldForm
          mode={mode}
          submitting={submitting}
          defaultColor={defaultColor}
          onCancel={() => onOpenChange(false)}
          onSubmit={onSubmit}
        />
      </DialogContent>
    </Dialog>
  );
}

/**
 * Errors are per-input rather than one toast: with four inputs spread across a
 * dialog and the document behind it, "check your input" does not say which.
 */
function AddFieldForm({
  mode,
  submitting,
  defaultColor,
  onCancel,
  onSubmit
}: {
  mode: AddFieldMode;
  submitting: boolean;
  defaultColor: string;
  onCancel: () => void;
  onSubmit: (draft: ManualFieldDraft) => void;
}) {
  const t = useTranslations('ocr');
  const [draft, setDraft] = useState<ManualFieldDraft>({
    name: '',
    value: '',
    color: defaultColor
  });
  const [errors, setErrors] = useState<ManualFieldDraftErrors>({});

  function handleSubmit() {
    // `bbox` is deliberately dropped: it does not exist yet at this step, and
    // reporting it as missing would flag an input this dialog does not own.
    const { bbox: _pendingBox, ...typed } = validateManualFieldDraft(
      draft,
      null
    );

    if (Object.keys(typed).length > 0) {
      setErrors(typed);
      return;
    }

    setErrors({});
    onSubmit({ ...draft, name: draft.name.trim(), value: draft.value.trim() });
  }

  const nameError = errors.name;
  const valueError = errors.value;

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t('addFieldTitle')}</DialogTitle>
        <DialogDescription>
          {mode === 'save'
            ? t('addFieldDescriptionNoRegion')
            : t('addFieldDescription')}
        </DialogDescription>
      </DialogHeader>

      <div className='space-y-4 py-1'>
        <div className='space-y-2'>
          <Label htmlFor='manual-field-name'>{t('addFieldNameLabel')}</Label>
          <Input
            id='manual-field-name'
            // The name is read off the document, so it is in the document's
            // language, not the interface's.
            dir='auto'
            value={draft.name}
            onChange={(event) => {
              const { value } = event.target;
              setDraft((prev) => ({ ...prev, name: value }));
              setErrors((prev) => ({ ...prev, name: undefined }));
            }}
            placeholder={t('addFieldNamePlaceholder')}
            aria-invalid={nameError != null}
            className={cn(nameError && 'border-destructive')}
          />
          {nameError && (
            <p className='text-destructive text-xs'>
              {nameError === 'required'
                ? t('addFieldNameRequired')
                : t('addFieldNameUnusable')}
            </p>
          )}
        </div>

        <div className='space-y-2'>
          <Label htmlFor='manual-field-value'>{t('addFieldValueLabel')}</Label>
          <Input
            id='manual-field-value'
            dir='auto'
            value={draft.value}
            onChange={(event) => {
              const { value } = event.target;
              setDraft((prev) => ({ ...prev, value }));
              setErrors((prev) => ({ ...prev, value: undefined }));
            }}
            placeholder={t('addFieldValuePlaceholder')}
            aria-invalid={valueError != null}
            className={cn(valueError && 'border-destructive')}
          />
          {valueError && (
            <p className='text-destructive text-xs'>
              {t('addFieldValueRequired')}
            </p>
          )}
        </div>

        <div className='space-y-2'>
          <Label>{t('addFieldColorLabel')}</Label>
          <div className='flex items-center gap-2'>
            {/* The shared picker — same wheel, same swatches, same hex input
                the field cards and the viewer's context menu already use. A
                second palette here would be a second answer to "what colours
                are available". */}
            <ColorPickerPopover
              value={draft.color}
              onChange={(hex) => setDraft((prev) => ({ ...prev, color: hex }))}
            />
            <span className='text-muted-foreground font-mono text-xs uppercase'>
              {draft.color}
            </span>
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button variant='outline' onClick={onCancel} disabled={submitting}>
          {t('cancel')}
        </Button>
        <Button onClick={handleSubmit} disabled={submitting}>
          {mode === 'save' ? (
            <>
              {submitting ? (
                <Loader2 className='me-1.5 h-4 w-4 animate-spin' />
              ) : (
                <Save className='me-1.5 h-4 w-4' />
              )}
              {t('addFieldSave')}
            </>
          ) : (
            <>
              <SquareDashedMousePointer className='me-1.5 h-4 w-4' />
              {t('addFieldDrawRegion')}
            </>
          )}
        </Button>
      </DialogFooter>
    </>
  );
}
